import { computed } from 'vue'
// types
import type { ComputedRef } from 'vue'
import type { BlockTutorCardProps } from '@ts-blocks/block-tutor-card/interfaces'
// hooks
import { useDefaultCtaUrl } from '@composables/tools/default-cta-url.hook'
import { useEventsSection } from '@hooks/events/sections/events-section.hook'

const CTA_FALLBACK_TEXT = 'Book free lesson'

interface BlockTutorCardHookOptions {
  block: () => BlockTutorCardProps
}

interface BlockTutorCardHookReturn {
  refCtaText: ComputedRef<string>
  refCtaHref: ComputedRef<string>
  refCtaTarget: ComputedRef<'_self' | '_blank'>
  handleClickCta: () => void
}

export const useBlockTutorCard = ({ block }: BlockTutorCardHookOptions): BlockTutorCardHookReturn => {
  const refDefaultCtaUrl = useDefaultCtaUrl()
  const { buildCtaHref, trackSectionEvent } = useEventsSection({ sectionName: 'tutors' })

  const refCtaText = computed<string>(() => block().button?.buttonText || CTA_FALLBACK_TEXT)

  const refBaseUrl = computed<string>(() => {
    const button = block().button
    if (button && button.isDefaultUrl === false) return button.buttonLink || refDefaultCtaUrl.value
    return refDefaultCtaUrl.value
  })

  const refCtaHref = computed<string>(() => buildCtaHref(refBaseUrl.value, { text: refCtaText.value }))

  const refCtaTarget = computed<'_self' | '_blank'>(() => (
    refBaseUrl.value === refDefaultCtaUrl.value ? '_self' : '_blank'
  ))

  const handleClickCta = (): void => {
    trackSectionEvent('Button_click', { name: 'book_lesson', text: refCtaText.value, url: refCtaHref.value })
  }

  return {
    refCtaText,
    refCtaHref,
    refCtaTarget,
    handleClickCta,
  }
}
